import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Svg } from '@hopskipdrive/react-components';
import { Button, ButtonLink } from '@rideiq/shared/components';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import { isEmpty } from 'lodash';

import { ReportsParamsAPI } from '../../../api/services/Reports';
import { formatDate } from '../../../utils/helpers/formatDate';
import { getFilters } from '../../helpers';
import { CustomReport, CustomReportInfo } from '../../types';

import { CustomReportForm } from './CustomReportForm/CustomReportForm';
import { reportingSchema } from './CustomReportForm/validationSchema';
import { CustomReportListPanel } from './CustomReportListPanel/CustomReportListPanel';

const defaultFilters = {
  name: '',
  date: [null, null],
  filterDate: '',
  statuses: [],
  riders: [],
  categories: [],
  tags: [],
  groupBy: '',
  favorite: false,
  default: false,
  uuid: '',
};

type ContextType = {
  customReports: CustomReport[];
  isSaving: boolean;
  isDeleting: boolean;
  updateCustomReport: (uuid: string, params: ReportsParamsAPI) => void;
  createCustomReport: (params: ReportsParamsAPI) => string;
  deleteCustomReport: (uuid: string) => string;
};

function useCustomReportModal() {
  return useOutletContext<ContextType>();
}

export const CustomReportModal: FC = () => {
  const location = useLocation();
  const filterForm = location.state?.filterForm;
  const [selectedId, setSelectedId] = useState('');
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };

  const methods = useForm<CustomReportInfo>({
    defaultValues: filterForm ? filterForm : defaultFilters,
    resolver: yupResolver(reportingSchema()),
  });

  const { errors } = methods.formState;

  const { customReports, isSaving, isDeleting, updateCustomReport, createCustomReport, deleteCustomReport } =
    useCustomReportModal();

  const handleSubmit = async (data: CustomReportInfo) => {
    const params = {
      name: data.name,
      ...(data.date?.[0] && { startDate: formatDate(new Date(data.date[0]), 'yyyy-MM-dd') }),
      ...(data.date?.[1] && { endDate: formatDate(new Date(data.date[1]), 'yyyy-MM-dd') }),
      ...(data.filterDate && { filterDate: data.filterDate }),
      statuses: data.statuses,
      riders: data.riders.map((r) => r.value),
      categories: data.categories.map((c) => c.value),
      ...(data.tags && { tags: data.tags.map((t) => t.value) }),
      groupBy: data.groupBy,
      favorite: data.favorite,
      default: data.default,
    };

    if (data.uuid) {
      updateCustomReport(data.uuid, params);
      methods.reset(methods.getValues());
    } else {
      const responseType = await createCustomReport(params);

      if (responseType === 'success') {
        resetForm();
      }
    }
  };

  const handleClick = (customReport) => {
    if (customReport) {
      const filters = getFilters(customReport);

      setSelectedId(customReport.uuid);

      methods.reset(filters);
    } else {
      resetForm();
    }
  };

  const handleDelete = async (uuid) => {
    const currentIndex = customReports.findIndex((r) => r.uuid === uuid);
    const filters = customReports[currentIndex + 1] ? getFilters(customReports[currentIndex + 1]) : null;

    if (id === uuid) {
      navigate('/reports/modal');
    }

    const responseType = await deleteCustomReport(uuid);

    if (responseType === 'success') {
      if (customReports.length > 1 && filters) {
        setSelectedId(customReports[currentIndex + 1].uuid);
        methods.reset(filters);
      } else {
        resetForm();
      }
    }
  };

  const resetForm = () => {
    setSelectedId('');
    methods.reset(defaultFilters);
  };

  return (
    <Modal open={true} maxWidth="xl">
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Custom Report Views
          <Link to=".." data-testid="closeButton" aria-label="Close custom report modal">
            <Svg height="20px" icon="close-outlined" />
          </Link>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Box display="flex">
          <CustomReportListPanel
            items={customReports}
            itemCount={customReports.length}
            selectedId={selectedId}
            handleClick={handleClick}
          />
          <FormProvider {...methods}>
            <FormA11yErrorAnnouncer errors={errors} />
            <CustomReportForm handleSubmit={handleSubmit} isDeleting={isDeleting} handleDelete={handleDelete} />
          </FormProvider>
        </Box>
      </Modal.Content>
      <Modal.Actions>
        <ButtonLink color="tertiary" to="..">
          Cancel
        </ButtonLink>
        <Button
          type="submit"
          color="primary"
          form="custom-report-form"
          disabled={isEmpty(methods.formState.dirtyFields)}
          loading={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
